import apiRequest from './apiRequest';

import type { Character } from './getCharacters';

export type ExternalCharacterUploadStatus =
  | 'ADJUSTMENT_FAILED'
  | 'AUTORIGGING_IN_PROGRESS'
  | 'COMPLETE'
  | 'FAILED'
  | 'NEEDS_VALIDATION'
  | 'PENDING'
  | 'RETARGETING_IN_PROGRESS';

export type ExternalCharacterListItem = {
  characterName: string;
  createdAt: string;
  createdBy: string;
  extension: string;
  jobID: string;
  uploadStatus?: ExternalCharacterUploadStatus;
  characterFileURL?: string | null;
  configURL?: string | null;
  tempConfigURL?: string | null;
  thumbnailURL?: string | null;
};

export type ExternalCharacterItem = Omit<
  Partial<ExternalCharacterListItem>,
  'uploadStatus'
> & {
  characterID?: string | null;
  objectID?: string | null;
  fileExtension?: string | null;
  heroThumbnailPath?: string | null;
  meshPath?: string | null;
  meshURL?: string | null;
  thumbnailPath?: string | null;
  uploadStatus?: ExternalCharacterUploadStatus | string;
};

export type ExternalCharacterListResponse = {
  characters: ExternalCharacterListItem[];
  nextToken?: string | null;
};

/** Reads `role_to_bone.Head` from a Cartographer-style mapping JSON object. */
export function parseHeadBoneFromMappingJson(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const roleToBone = (data as { role_to_bone?: unknown }).role_to_bone;
  if (!roleToBone || typeof roleToBone !== 'object' || Array.isArray(roleToBone)) {
    return undefined;
  }
  const head = (roleToBone as Record<string, unknown>).Head;
  if (typeof head !== 'string') {
    return undefined;
  }
  const trimmed = head.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export async function fetchHeadBoneNameFromConfigUrl(
  configUrl: string,
): Promise<string | undefined> {
  try {
    const response = await fetch(configUrl);
    if (!response.ok) {
      return undefined;
    }
    const data: unknown = await response.json();
    return parseHeadBoneFromMappingJson(data);
  } catch {
    return undefined;
  }
}

export default async function getExternalCharacters(
  apiKey: string,
  params?: { limit?: number; nextToken?: string; sortAscending?: boolean },
) {
  const urlParams = new URLSearchParams();
  if (params?.limit != null) {
    urlParams.set('limit', params.limit.toString());
  }
  if (params?.nextToken) {
    urlParams.set('nextToken', params.nextToken);
  }
  if (params?.sortAscending != null) {
    urlParams.set('sortAscending', params.sortAscending.toString());
  }
  const query = urlParams.toString() ? `?${urlParams.toString()}` : '';

  return apiRequest<ExternalCharacterListResponse>(
    'external-mogen',
    `/characters${query}`,
    {
      method: 'GET',
      headers: {
        'x-api-key': apiKey,
      },
    },
  );
}

/**
 * External `/characters` returns `characterFileURL`; Motion Editor + Player expect
 * a {@link Character} shape (`meshPath`, thumbnails, etc.).
 *
 * This is a plain object (not a `ConsumerCharacterAccessRecord` instance): importing
 * `dynamo-daos` here would pull Node-only code (`process`) into the browser bundle.
 */
export function mapExternalCharacterListItemToCharacter(
  item: ExternalCharacterListItem,
): Character {
  const character = mapExternalCharacterItemToCharacter(item);
  if (!character) {
    throw new Error('External character list item is missing a character ID.');
  }

  return character;
}

export function mapExternalCharacterItemToCharacter(
  item: ExternalCharacterItem,
): Character | null {
  const jobID =
    item.jobID?.trim() ||
    item.characterID?.trim() ||
    item.objectID?.trim() ||
    '';
  if (!jobID) {
    return null;
  }

  const meshPath =
    item.characterFileURL?.trim() ||
    item.meshURL?.trim() ||
    item.meshPath?.trim() ||
    '';
  const thumbnailURL =
    item.thumbnailURL?.trim() ||
    item.heroThumbnailPath?.trim() ||
    item.thumbnailPath?.trim() ||
    '';
  const characterConfigURL =
    item.configURL?.trim() || item.tempConfigURL?.trim() || undefined;
  const createdBy = item.createdBy?.trim() || '';
  const createdAt = item.createdAt?.trim() || '';
  return {
    accessorID: createdBy,
    accessLevel: 'VIEW',
    characterName: item.characterName?.trim() || jobID,
    createdAt,
    createdBy,
    jobCreatedBy: createdBy,
    jobID,
    lastUpdatedAt: createdAt,
    PK: jobID,
    SK: `ACCESS#${createdBy}`,
    meshPath,
    heroThumbnailPath: thumbnailURL,
    thumbnailPath: thumbnailURL,
    markerAdjustmentEligible: false,
    mappingAdjustmentEligible: false,
    uploadStatus: resolveExternalCharacterUploadStatus(item),
    vertexCount: 0,
    extension: item.extension?.trim() || item.fileExtension?.trim() || '',
    meshData: { current: null },
    readyOn: null,
    ...(characterConfigURL ? { characterConfigURL } : {}),
  } as unknown as Character;
}

function resolveExternalCharacterUploadStatus(
  item: ExternalCharacterItem,
): ExternalCharacterUploadStatus {
  if (item.uploadStatus) {
    return item.uploadStatus as ExternalCharacterUploadStatus;
  }

  // Listed characters with a downloadable mesh are usable in the editor.
  return item.characterFileURL?.trim() ? 'COMPLETE' : 'PENDING';
}
